import React, { useState } from 'react';
import { UserProfile } from '../../types';
import { AppSettings } from '../../lib/storage';
import { Camera, Save, User, Smartphone, Shield, LogOut, Check, X, AlertTriangle } from 'lucide-react';
import { soundEffects } from '../../lib/soundEffects';

interface Props {
  currentUser: UserProfile;
  onUpdateProfile: (profile: Partial<UserProfile>) => void;
  onLogout: () => void;
  onTerminateSession: (deviceId: string) => void;
  onLogoutAllDevices: () => void;
}

export const AccountSection: React.FC<Props> = ({ currentUser, onUpdateProfile, onLogout, onTerminateSession, onLogoutAllDevices }) => {
  const [displayName, setDisplayName] = useState(currentUser.displayName);
  const [bio, setBio] = useState(currentUser.bio || '');
  const [phone, setPhone] = useState(currentUser.phone || '');
  const [email, setEmail] = useState(currentUser.email || '');
  
  // Username state
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'restricted'>('idle');
  const [usernameMessage, setUsernameMessage] = useState('');
  const [savingUsername, setSavingUsername] = useState(false);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);

  // Avatar ref
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleSaveProfile = async () => {
    soundEffects.playTap();
    setIsSaving(true);
    await onUpdateProfile({ displayName, bio, phone, email });
    setIsSaving(false);
    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 2000);
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        onUpdateProfile({ avatar: base64String });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setNewUsername(val);
    if (val.length < 3) {
      setUsernameStatus('invalid');
      setUsernameMessage('يجب أن يكون 3 أحرف على الأقل');
      return;
    }
    
    setUsernameStatus('checking');
    setUsernameMessage('جاري التحقق...');
    
    // Check availability
    clearTimeout((window as any).usernameCheckTimeout);
    (window as any).usernameCheckTimeout = setTimeout(async () => {
      try {
        const res = await fetch(`/api/users/check-username?q=${val}`);
        const data = await res.json();
        if (data.available) {
          setUsernameStatus('available');
          setUsernameMessage('اسم المستخدم متاح ✓');
        } else {
          setUsernameStatus(data.reason as any);
          if (data.reason === 'taken') setUsernameMessage('اسم المستخدم مستخدم مسبقاً');
          else if (data.reason === 'restricted') setUsernameMessage('هذا الاسم محجوز للنظام');
          else setUsernameMessage('اسم المستخدم غير صالح');
        }
      } catch {
        setUsernameStatus('idle');
        setUsernameMessage('');
      }
    }, 500);
  };

  const saveUsername = async () => {
    if (usernameStatus !== 'available') return;
    setSavingUsername(true);
    try {
      const res = await fetch(`/api/users/${currentUser.username}/change-username`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newUsername })
      });
      const data = await res.json();
      if (data.success) {
        alert('تم تغيير اسم المستخدم بنجاح! سيتم إعادة تحديث الجلسة.');
        window.location.reload();
      } else {
        alert(data.error || 'حدث خطأ أثناء تغيير اسم المستخدم');
      }
    } catch {
      alert('فشل الاتصال بالخادم.');
    }
    setSavingUsername(false);
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="bg-[#0e1422] rounded-3xl p-5 border border-slate-800/80 shadow-lg">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-emerald-400" />
          معلومات الحساب
        </h3>

        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4 mb-2">
            <div className="relative w-20 h-20 rounded-full border-2 border-slate-700 p-1 group">
              <img src={currentUser.avatar} alt={currentUser.displayName} className="w-full h-full rounded-full object-cover" />
              <button onClick={() => fileInputRef.current?.click()} className="absolute bottom-0 right-0 p-1.5 bg-emerald-500 rounded-full text-black hover:scale-110 transition shadow-lg">
                <Camera className="w-3.5 h-3.5" />
              </button>
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleAvatarChange} 
                accept="image/*" 
                className="hidden" 
              />
            </div>
            <div>
              <div className="text-sm text-slate-400 mb-1">اسم المستخدم (@username)</div>
              <button onClick={() => { setNewUsername(''); setUsernameStatus('idle'); setUsernameMessage(''); setShowUsernameModal(true); }} className="text-emerald-400 font-bold hover:underline flex items-center gap-1">
                @{currentUser.username}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1.5">الاسم الظاهر</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full bg-[#141c2a] border border-slate-700 rounded-xl px-3 py-2 text-white focus:border-emerald-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1.5">النبذة (Bio)</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              className="w-full bg-[#141c2a] border border-slate-700 rounded-xl px-3 py-2 text-white resize-none focus:border-emerald-500 focus:outline-none"
              rows={2}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1.5">رقم الهاتف</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="غير محدد"
              className="w-full bg-[#141c2a] border border-slate-700 rounded-xl px-3 py-2 text-white focus:border-emerald-500 focus:outline-none text-left"
              dir="ltr"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1.5">البريد الإلكتروني</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="غير محدد"
              className="w-full bg-[#141c2a] border border-slate-700 rounded-xl px-3 py-2 text-white focus:border-emerald-500 focus:outline-none text-left"
              dir="ltr"
            />
          </div>

          <button disabled={isSaving} onClick={handleSaveProfile} className={`mt-2 w-full py-2.5 font-bold rounded-xl flex items-center justify-center gap-2 transition active:scale-95 ${savedSuccess ? 'bg-emerald-400 text-slate-900' : 'bg-emerald-500 hover:bg-emerald-400 text-slate-950 disabled:opacity-50'}`}>
            {savedSuccess ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            {isSaving ? 'جاري الحفظ...' : savedSuccess ? 'تم الحفظ' : 'حفظ التغييرات'}
          </button>
        </div>
      </div>

      <div className="bg-[#0e1422] rounded-3xl p-5 border border-slate-800/80 shadow-lg">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-indigo-400" />
          الأجهزة والجلسات
        </h3>
        
        <div className="space-y-3">
          {currentUser.activeSessions?.map((session) => (
            <div key={session.deviceId} className="flex items-center justify-between p-3 rounded-xl bg-[#141c2a] border border-slate-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                  <Smartphone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-white">{session.deviceName}</div>
                  <div className="text-xs text-slate-400 flex items-center gap-2">
                    <span>{session.ip}</span>
                    <span className="w-1 h-1 rounded-full bg-slate-600"></span>
                    <span>{new Date(session.lastActive).toLocaleDateString('ar-SA')}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => onTerminateSession(session.deviceId)}
                className="px-3 py-1.5 rounded-lg bg-rose-500/10 text-rose-400 text-xs font-bold hover:bg-rose-500/20 transition"
              >
                إنهاء
              </button>
            </div>
          ))}
          <button onClick={onLogoutAllDevices} className="w-full mt-2 py-2.5 border border-rose-500/30 text-rose-400 font-bold rounded-xl hover:bg-rose-500/10 transition flex items-center justify-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            تسجيل الخروج من الأجهزة الأخرى
          </button>
        </div>
      </div>

      {showUsernameModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#0e1422] rounded-3xl p-6 border border-slate-800 w-full max-w-sm">
            <h3 className="text-lg font-bold text-white mb-2">تغيير اسم المستخدم</h3>
            <p className="text-xs text-slate-400 mb-4">
              يمكنك اختيار اسم مستخدم جديد. سيتم تحديث روابطك تلقائياً لتصبح <br/>
              <span className="text-emerald-400" dir="ltr">wateen.app/@{newUsername || 'username'}</span>
            </p>
            <div className="relative mb-2">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-bold">@</span>
              <input
                type="text"
                value={newUsername}
                onChange={handleUsernameChange}
                placeholder="اسم المستخدم الجديد"
                className="w-full bg-[#141c2a] border border-slate-700 rounded-xl pl-8 pr-3 py-2 text-white focus:border-emerald-500 focus:outline-none text-left"
                dir="ltr"
              />
            </div>
            {usernameMessage && (
              <div className={`text-xs mb-4 ${usernameStatus === 'available' ? 'text-emerald-400' : usernameStatus === 'checking' ? 'text-slate-400' : 'text-rose-400'}`}>
                {usernameMessage}
              </div>
            )}
            
            <div className="flex gap-2">
              <button 
                onClick={() => setShowUsernameModal(false)}
                className="flex-1 py-2 rounded-xl bg-slate-800 text-white font-bold hover:bg-slate-700 transition"
              >
                إلغاء
              </button>
              <button 
                disabled={usernameStatus !== 'available' || savingUsername}
                onClick={saveUsername}
                className="flex-1 py-2 rounded-xl bg-emerald-500 text-slate-950 font-bold hover:bg-emerald-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingUsername ? 'جاري الحفظ...' : 'تأكيد وحفظ'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
